'use client';
import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import PriorityBadge from '@/components/ui/PriorityBadge';
import ChannelBadge from '@/components/ui/ChannelBadge';

// Backend integration point: replace with /api/cases
const allCases = [
  { id: 'MYGCC-2026-008432', citizen: 'Ahmed Firdaus', topic: 'MySejahtera Mobile Number Update', agency: 'KKM', channel: 'PHONE' as const, priority: 'Medium' as const, status: 'New', assignedTo: 'Nur Aisyah', created: 'Today', sla: 'OK', slaRemaining: '8h 00m', description: 'Citizen contacted MyGCC regarding updating the registered mobile number associated with their MySejahtera account. Identity information was verified and approved KKM guidance was provided.' },
  { id: 'MYGCC-2026-008420', citizen: 'Kumar Rajan', topic: 'Road Tax Renewal', agency: 'JPJ', channel: 'PHONE' as const, priority: 'Medium' as const, status: 'In Progress', assignedTo: 'Nur Aisyah', created: 'Today', sla: 'At Risk', slaRemaining: '2h 14m', description: 'Citizen requires assistance with road tax renewal process.' },
  { id: 'MYGCC-2026-008418', citizen: 'Nur Aina', topic: 'MyDigital ID Setup Assistance', agency: 'JPN', channel: 'LIVE CHAT' as const, priority: 'Low' as const, status: 'Awaiting Citizen', assignedTo: 'Nur Aisyah', created: 'Today', sla: 'OK', slaRemaining: '4h 30m', description: 'Citizen requires assistance setting up MyDigital ID account.' },
  { id: 'MYGCC-2026-008415', citizen: 'Muhammad Danish', topic: 'KWSP Withdrawal Eligibility', agency: 'KWSP', channel: 'PHONE' as const, priority: 'High' as const, status: 'Awaiting Agency', assignedTo: 'Nur Aisyah', created: 'Yesterday', sla: 'Breach', slaRemaining: 'Overdue', description: 'Citizen enquired about KWSP withdrawal eligibility under special scheme.' },
  { id: 'MYGCC-2026-008409', citizen: 'Farah Lim', topic: 'Income Tax Filing Assistance', agency: 'LHDNM', channel: 'EMAIL' as const, priority: 'Medium' as const, status: 'In Progress', assignedTo: 'Nur Aisyah', created: 'Yesterday', sla: 'OK', slaRemaining: '6h 00m', description: 'Citizen requires assistance with income tax filing process.' },
  { id: 'MYGCC-2026-008401', citizen: 'Azri Hamdan', topic: 'MySejahtera Account Update', agency: 'KKM', channel: 'WHATSAPP' as const, priority: 'Low' as const, status: 'Resolved', assignedTo: 'Hafizuddin Razali', created: '2 days ago', sla: 'Met', slaRemaining: 'Resolved', description: 'Citizen required assistance updating MySejahtera account information.' },
  { id: 'MYGCC-2026-008390', citizen: 'Priya Sundaram', topic: 'Passport Application Status', agency: 'JIM', channel: 'PHONE' as const, priority: 'High' as const, status: 'Awaiting Agency', assignedTo: 'Siti Rohaida', created: '2 days ago', sla: 'At Risk', slaRemaining: '1h 05m', description: 'Citizen enquired about passport application status.' },
  { id: 'MYGCC-2026-008382', citizen: 'Zulkifli Othman', topic: 'PERKESO Claim Submission', agency: 'PERKESO', channel: 'EMAIL' as const, priority: 'High' as const, status: 'New', assignedTo: 'Unassigned', created: '2 days ago', sla: 'OK', slaRemaining: '8h 00m', description: 'Citizen requires assistance with PERKESO claim submission.' },
  { id: 'MYGCC-2026-008375', citizen: 'Mei Ling Tan', topic: 'STR Application Follow-up', agency: 'KPKT', channel: 'WHATSAPP' as const, priority: 'Medium' as const, status: 'In Progress', assignedTo: 'Ahmad Shahril', created: '3 days ago', sla: 'OK', slaRemaining: '5h 20m', description: 'Citizen following up on STR application status.' },
  { id: 'MYGCC-2026-008312', citizen: 'Rajendran Pillai', topic: 'MyDigital ID Verification Issue', agency: 'JPN', channel: 'PHONE' as const, priority: 'High' as const, status: 'Closed', assignedTo: 'Siti Rohaida', created: '5 days ago', sla: 'Met', slaRemaining: 'Closed', description: 'Citizen experienced issues with MyDigital ID verification process.' },
];

const linkedTickets: Record<string, { id: string; title: string; status: string }[]> = {
  'MYGCC-2026-008432': [{ id: 'MYGCC-TKT-2026-010482', title: 'MySejahtera Mobile Number Update', status: 'Open' }],
  'MYGCC-2026-008420': [{ id: 'MYGCC-TKT-2026-010480', title: 'Road Tax Renewal Assistance', status: 'Open' }],
  'MYGCC-2026-008401': [{ id: 'MYGCC-TKT-2026-010211', title: 'MySejahtera Account Update', status: 'Resolved' }],
};

type SortField = 'id' | 'citizen' | 'agency' | 'priority' | 'status' | 'created' | 'sla';
type SortDir = 'asc' | 'desc';

const statusOptions = ['All', 'New', 'Open', 'In Progress', 'Awaiting Citizen', 'Awaiting Agency', 'Resolved', 'Closed'];
const agencyOptions = ['All', 'MOH', 'JPJ', 'KWSP', 'LHDN', 'JPN', 'Immigration', 'PERKESO', 'KPKT'];
const priorityOptions = ['All', 'Critical', 'High', 'Medium', 'Low'];
const channelOptions = ['All', 'PHONE', 'WHATSAPP', 'LIVE CHAT', 'EMAIL', 'WEB CHAT'];

type CaseItem = typeof allCases[0];

function CaseDetailView({ caseItem, onBack }: { caseItem: CaseItem; onBack: () => void }) {
  const tickets = linkedTickets[caseItem.id] || [];
  return (
    <div className="max-w-screen-lg mx-auto px-6 py-5">
      <button onClick={onBack} className="flex items-center gap-1.5 text-[12px] text-primary font-600 hover:underline mb-4">
        <Icon name="ArrowLeftIcon" size={13} />
        Back to Cases
      </button>

      {/* Case Header */}
      <div className="card-elevated p-5 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[12px] font-700 text-primary mb-0.5">{caseItem.id}</div>
            <h1 className="text-[18px] font-700 text-foreground">{caseItem.topic}</h1>
          </div>
          <div className="flex items-center gap-2">
            <PriorityBadge priority={caseItem.priority} />
            <StatusBadge status={caseItem.status} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-[12px]">
          {[
            { label: 'Customer', value: caseItem.citizen },
            { label: 'Agency', value: caseItem.agency },
            { label: 'Channel', value: caseItem.channel },
            { label: 'Assigned Officer', value: caseItem.assignedTo },
            { label: 'Created Date', value: caseItem.created },
            { label: 'Last Updated', value: caseItem.created },
            { label: 'SLA Status', value: caseItem.slaRemaining },
          ].map(f => (
            <div key={f.label}>
              <div className="text-muted-foreground text-[10px] mb-0.5">{f.label}</div>
              <div className="font-600 text-foreground">{f.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-4">
          {/* Description */}
          <div className="card-elevated p-4">
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Case Description</div>
            <p className="text-[13px] text-foreground leading-relaxed">{caseItem.description}</p>
          </div>

          {/* Activity History */}
          <div className="card-elevated p-4">
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-3">Activity History</div>
            <div className="space-y-3">
              {[
                { time: '14:32', action: 'Case created by Nur Aisyah', type: 'create' },
                { time: '14:35', action: 'AI analysis completed — Intent: ' + caseItem.topic, type: 'ai' },
                { time: '14:40', action: 'Assigned to ' + caseItem.assignedTo, type: 'assign' },
              ].map((a, i) => (
                <div key={i} className="flex items-start gap-3 text-[12px]">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${a.type === 'ai' ? 'bg-primary/10' : 'bg-muted'}`}>
                    <Icon name={a.type === 'ai' ? 'SparklesIcon' : a.type === 'create' ? 'PlusIcon' : 'UserIcon'} size={11} className={a.type === 'ai' ? 'text-primary' : 'text-muted-foreground'} />
                  </div>
                  <div>
                    <div className="text-foreground">{a.action}</div>
                    <div className="text-muted-foreground text-[10px]">Today, {a.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Linked Tickets */}
          {tickets.length > 0 && (
            <div className="card-elevated p-4">
              <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-3">Linked Tickets</div>
              {tickets.map(t => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <div className="text-[12px] font-700 text-primary">{t.id}</div>
                    <div className="text-[11px] text-muted-foreground">{t.title}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={t.status} />
                    <button onClick={() => toast.info(`Opening ticket ${t.id}`)} className="btn-secondary px-2 py-1 text-[11px]">Open</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          <div className="card-elevated p-4">
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-3">Actions</div>
            <div className="space-y-2">
              <button onClick={() => toast.success('Case updated')} className="w-full btn-primary py-2 text-[12px]">Update Case</button>
              <button onClick={() => toast.info('Interaction linked')} className="w-full btn-secondary py-2 text-[12px]">Link Interaction</button>
              <button onClick={() => toast.info('Case escalated')} className="w-full btn-ghost py-2 text-[12px] border border-border">Escalate</button>
              <button onClick={() => toast.success('Case resolved')} className="w-full btn-ghost py-2 text-[12px] border border-green-200 text-green-700">Resolve</button>
            </div>
          </div>
          <div className="card-elevated p-4">
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">MIR Recording</div>
            <div className="text-[11px] font-mono text-muted-foreground">MIR-20260816-14892</div>
            <button onClick={() => toast.info('Playing recording')} className="mt-2 btn-secondary px-3 py-1.5 text-[11px] flex items-center gap-1.5">
              <Icon name="PlayIcon" size={11} />
              Play Recording
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CasesContent() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterAgency, setFilterAgency] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterChannel, setFilterChannel] = useState('All');
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedCase, setSelectedCase] = useState<CaseItem | null>(null);

  if (selectedCase) {
    return <CaseDetailView caseItem={selectedCase} onBack={() => setSelectedCase(null)} />;
  }

  const filtered = allCases.filter((c) => {
    const matchSearch = !search || c.id.toLowerCase().includes(search.toLowerCase()) || c.citizen.toLowerCase().includes(search.toLowerCase()) || c.topic.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'All' || c.status === filterStatus;
    const matchAgency = filterAgency === 'All' || c.agency === filterAgency;
    const matchPriority = filterPriority === 'All' || c.priority === filterPriority;
    const matchChannel = filterChannel === 'All' || c.channel === filterChannel;
    return matchSearch && matchStatus && matchAgency && matchPriority && matchChannel;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortField] as string;
    const bv = b[sortField] as string;
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const paginated = sorted.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(sorted.length / perPage);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleRow = (id: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === paginated.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(paginated.map((c) => c.id)));
  };

  const getSlaClass = (sla: string) => {
    if (sla === 'Breach') return 'text-red-600';
    if (sla === 'At Risk') return 'text-amber-600';
    if (sla === 'Met' || sla === 'Resolved' || sla === 'Closed') return 'text-green-600';
    return 'text-muted-foreground';
  };

  const columns: { key: SortField; label: string }[] = [
    { key: 'id', label: 'Case ID' },
    { key: 'citizen', label: 'Citizen' },
    { key: 'agency', label: 'Agency' },
    { key: 'priority', label: 'Priority' },
    { key: 'status', label: 'Status' },
    { key: 'created', label: 'Created' },
    { key: 'sla', label: 'SLA' },
  ];

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-700 text-foreground">Cases</h1>
          <p className="text-[13px] text-muted-foreground mt-0.5">
            {filtered.length} cases · {allCases.filter(c => c.status === 'In Progress').length} in progress · {allCases.filter(c => c.sla === 'Breach').length} SLA breach
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => toast.info('Exporting cases')} className="btn-ghost px-3 py-1.5 border border-border flex items-center gap-1.5 text-[13px]">
            <Icon name="ArrowDownTrayIcon" size={14} />
            Export
          </button>
          <button onClick={() => toast.info('Creating new case')} className="btn-primary px-3 py-1.5 flex items-center gap-1.5 text-[13px]">
            <Icon name="PlusIcon" size={14} />
            New Case
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card-elevated p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search cases, citizens, topics..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-8 pr-3 py-1.5 text-[13px] border border-border rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} className="px-2.5 py-1.5 text-[12px] border border-border rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-primary">
            {statusOptions.map((s) => <option key={`fstat-${s}`} value={s}>{s === 'All' ? 'All Statuses' : s}</option>)}
          </select>
          <select value={filterAgency} onChange={(e) => { setFilterAgency(e.target.value); setPage(1); }} className="px-2.5 py-1.5 text-[12px] border border-border rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-primary">
            {agencyOptions.map((a) => <option key={`fagency-${a}`} value={a}>{a === 'All' ? 'All Agencies' : a}</option>)}
          </select>
          <select value={filterPriority} onChange={(e) => { setFilterPriority(e.target.value); setPage(1); }} className="px-2.5 py-1.5 text-[12px] border border-border rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-primary">
            {priorityOptions.map((p) => <option key={`fpri-${p}`} value={p}>{p === 'All' ? 'All Priorities' : p}</option>)}
          </select>
          <select value={filterChannel} onChange={(e) => { setFilterChannel(e.target.value); setPage(1); }} className="px-2.5 py-1.5 text-[12px] border border-border rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-primary">
            {channelOptions.map((c) => <option key={`fchan-${c}`} value={c}>{c === 'All' ? 'All Channels' : c}</option>)}
          </select>
          {(search || filterStatus !== 'All' || filterAgency !== 'All' || filterPriority !== 'All' || filterChannel !== 'All') && (
            <button onClick={() => { setSearch(''); setFilterStatus('All'); setFilterAgency('All'); setFilterPriority('All'); setFilterChannel('All'); setPage(1); }}
              className="btn-ghost px-2 py-1.5 text-[12px] flex items-center gap-1 text-muted-foreground">
              <Icon name="XMarkIcon" size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedRows.size > 0 && (
        <div className="flex items-center gap-3 bg-primary/10 border border-primary/30 rounded-xl px-4 py-2.5 mb-3">
          <span className="text-[13px] font-600 text-primary">{selectedRows.size} case{selectedRows.size > 1 ? 's' : ''} selected</span>
          <div className="flex items-center gap-2 ml-2">
            {[{ label: 'Assign', icon: 'UserIcon' }, { label: 'Change Status', icon: 'ArrowPathIcon' }, { label: 'Export', icon: 'ArrowDownTrayIcon' }, { label: 'Close', icon: 'ArchiveBoxIcon' }].map((action) => (
              <button key={`bulk-${action.label}`} onClick={() => toast.info(`${action.label} applied to ${selectedRows.size} cases`)}
                className="btn-secondary px-2.5 py-1 text-[11px] flex items-center gap-1">
                <Icon name={action.icon as Parameters<typeof Icon>[0]['name']} size={12} />
                {action.label}
              </button>
            ))}
          </div>
          <button onClick={() => setSelectedRows(new Set())} className="ml-auto text-muted-foreground hover:text-foreground">
            <Icon name="XMarkIcon" size={14} />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="px-3 py-2.5 w-10">
                  <input type="checkbox" checked={selectedRows.size === paginated.length && paginated.length > 0} onChange={toggleAll} className="rounded border-border" />
                </th>
                {columns.map((col) => (
                  <th key={`col-${col.key}`}
                    className="text-left px-3 py-2.5 text-[11px] font-600 uppercase tracking-wide text-muted-foreground whitespace-nowrap cursor-pointer hover:text-foreground transition-colors select-none"
                    onClick={() => handleSort(col.key)}>
                    <div className="flex items-center gap-1">
                      {col.label}
                      {sortField === col.key ? (
                        <Icon name={sortDir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={11} className="text-primary" />
                      ) : (
                        <Icon name="ChevronUpDownIcon" size={11} className="text-muted-foreground/50" />
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2.5 text-left text-[11px] font-600 uppercase tracking-wide text-muted-foreground whitespace-nowrap">Channel</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-600 uppercase tracking-wide text-muted-foreground whitespace-nowrap">Topic</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-600 uppercase tracking-wide text-muted-foreground whitespace-nowrap">Assigned To</th>
                <th className="px-3 py-2.5 w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center">
                    <Icon name="ClipboardDocumentListIcon" size={32} className="text-muted-foreground/40 mx-auto mb-2" />
                    <div className="text-[13px] font-600 text-muted-foreground">No cases match your filters</div>
                  </td>
                </tr>
              ) : (
                paginated.map((c) => (
                  <tr key={c.id}
                    className={`hover:bg-muted/30 transition-colors group cursor-pointer ${selectedRows.has(c.id) ? 'bg-secondary/50' : ''}`}
                    onClick={() => setSelectedCase(c)}
                  >
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedRows.has(c.id)} onChange={() => toggleRow(c.id)} className="rounded border-border" />
                    </td>
                    <td className="px-3 py-3 font-600 text-primary whitespace-nowrap text-[12px]">{c.id}</td>
                    <td className="px-3 py-3 text-foreground whitespace-nowrap font-500">{c.citizen}</td>
                    <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{c.agency}</td>
                    <td className="px-3 py-3"><PriorityBadge priority={c.priority} /></td>
                    <td className="px-3 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{c.created}</td>
                    <td className={`px-3 py-3 font-tabular font-600 whitespace-nowrap text-[12px] ${getSlaClass(c.sla)}`}>{c.slaRemaining}</td>
                    <td className="px-3 py-3"><ChannelBadge channel={c.channel} /></td>
                    <td className="px-3 py-3 text-muted-foreground max-w-[180px] truncate">{c.topic}</td>
                    <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{c.assignedTo}</td>
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button title="Open Case" onClick={() => setSelectedCase(c)} className="p-1.5 rounded hover:bg-secondary transition-colors">
                          <Icon name="EyeIcon" size={13} className="text-primary" />
                        </button>
                        <button title="Edit Case" onClick={() => toast.info(`Editing ${c.id}`)} className="p-1.5 rounded hover:bg-secondary transition-colors">
                          <Icon name="PencilIcon" size={13} className="text-muted-foreground" />
                        </button>
                        <button title="Escalate" onClick={() => toast.info(`Escalating ${c.id}`)} className="p-1.5 rounded hover:bg-secondary transition-colors">
                          <Icon name="ArrowUpCircleIcon" size={13} className="text-amber-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <span>Show</span>
            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="px-2 py-1 border border-border rounded text-[12px] bg-background focus:outline-none">
              {[10, 20, 50].map((n) => <option key={`pp-${n}`} value={n}>{n}</option>)}
            </select>
            <span>per page · {sorted.length} total</span>
          </div>
          <div className="flex items-center gap-1">
            <button disabled={page === 1} onClick={() => setPage(1)} className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-30">
              <Icon name="ChevronDoubleLeftIcon" size={13} className="text-muted-foreground" />
            </button>
            <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-30">
              <Icon name="ChevronLeftIcon" size={13} className="text-muted-foreground" />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pg = i + 1;
              return (
                <button key={`pg-${pg}`} onClick={() => setPage(pg)}
                  className={`w-7 h-7 rounded text-[12px] font-600 transition-colors ${page === pg ? 'bg-primary text-white' : 'hover:bg-muted text-muted-foreground'}`}>
                  {pg}
                </button>
              );
            })}
            <button disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-30">
              <Icon name="ChevronRightIcon" size={13} className="text-muted-foreground" />
            </button>
            <button disabled={page === totalPages} onClick={() => setPage(totalPages)} className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-30">
              <Icon name="ChevronDoubleRightIcon" size={13} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}